import React, { useState, useCallback, useMemo } from 'react';
import { Bell, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../ui/dropdown.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select.tsx';
import { useStaffNotificationsContext } from '../../../contexts/StaffNotificationsContext.jsx';
import StaffNotificationItem from '../staff-notification-item.jsx';
import { LoadingSpinner } from '../../ui/loading-spinner.tsx';
import { NotificationErrorBoundary } from './NotificationErrorBoundary.jsx';

export default function StaffNotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  const {
    notifications,
    isLoading,
    unreadCount,
    handleNotificationClick,
    handleMarkAllAsRead,
    handleClearAll,
    handleRemoveNotification,
  } = useStaffNotificationsContext();

  const filteredNotifications = useMemo(() => {
    return notifications
      .filter((notification) => {
        if (typeFilter !== 'all' && notification.type !== typeFilter)
          return false;
        if (
          priorityFilter !== 'all' &&
          notification.priority !== priorityFilter
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.date || a.time);
        const dateB = new Date(b.createdAt || b.date || b.time);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
  }, [notifications, typeFilter, priorityFilter, sortOrder]);

  const handleMarkAllReadClick = useCallback(() => {
    handleMarkAllAsRead();
    setIsOpen(false);
  }, [handleMarkAllAsRead]);

  const handleClearNotifications = useCallback(() => {
    handleClearAll();
    setIsOpen(false);
  }, [handleClearAll]);

  const handleNotificationItemClick = useCallback(
    async (notification) => {
      await handleNotificationClick(notification);
      setIsOpen(false);
    },
    [handleNotificationClick]
  );

  const handleDeleteNotification = useCallback(
    (notification, e) => {
      // Stop the event from bubbling to parent handlers
      if (e) {
        e.stopPropagation();
      }
      handleRemoveNotification(
        notification.notificationId || notification.id,
        e
      );
    },
    [handleRemoveNotification]
  );

  return (
    <NotificationErrorBoundary>
      <div className="flex items-center gap-2">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <button className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-full">
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-pulse" />
              )}
              <Bell className="w-6 h-6" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="w-80 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-[70vh] overflow-y-auto"
          >
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                {notifications.length > 0 && (
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <span className="text-xs text-gray-500">
                        {unreadCount} new
                      </span>
                    )}
                    <button
                      onClick={handleClearNotifications}
                      className="p-1 text-gray-400 hover:text-red-500 focus:outline-none rounded"
                      title="Clear all notifications"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 mt-2">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="alert">Alert</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={priorityFilter}
                  onValueChange={setPriorityFilter}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Filter by priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <button
                  onClick={() =>
                    setSortOrder((prev) =>
                      prev === 'newest' ? 'oldest' : 'newest'
                    )
                  }
                  className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none rounded"
                  title={`Sort by ${
                    sortOrder === 'newest' ? 'oldest' : 'newest'
                  } first`}
                >
                  {sortOrder === 'newest' ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="py-2">
              {isLoading ? (
                <div className="px-4 py-6 text-center">
                  <LoadingSpinner size="md" />
                  <p className="text-sm text-gray-500 mt-2">
                    Loading notifications...
                  </p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <div
                    key={notification.notificationId || notification.id}
                    className="px-2"
                  >
                    <StaffNotificationItem
                      notification={notification}
                      isHovered={
                        hoveredIndex ===
                        (notification.notificationId || notification.id)
                      }
                      onHover={() =>
                        setHoveredIndex(
                          notification.notificationId || notification.id
                        )
                      }
                      onLeave={() => setHoveredIndex(null)}
                      onClick={() => handleNotificationItemClick(notification)}
                      onDelete={(e) =>
                        handleDeleteNotification(notification, e)
                      }
                    />
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-gray-500">
                    {notifications.length === 0
                      ? 'No notifications'
                      : 'No notifications match the current filters'}
                  </p>
                </div>
              )}
            </div>

            {notifications.length > 0 && unreadCount > 0 && (
              <div className="px-4 py-2 border-t border-gray-100">
                <button
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium w-full text-center"
                  onClick={handleMarkAllReadClick}
                >
                  Mark all as read
                </button>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </NotificationErrorBoundary>
  );
}
